import { EditorIntegration } from '@/infrastructure/editor-platform/editor-integration';
import { IEditorOperations } from '@/platform/editor/i-editor-operations';
import { ref, Ref, toRaw } from 'vue';
import ScholarshipFormModel from '@/presentation/components/scholarship-form/scholarship-form-model';
import { PlatformIntegration } from '@/platform/platform-integration';
import ScholarshipService from '../../services/scholarship-service';
import ScholarshipPresenter from '@/presenters/scholarship-presenter';
import { toPresence, toScholarshipInfo } from '../components/scholarship-form/scholarship-form-model-extensions';
import { Scholarship } from '@/presenters/scholarship';

export default class ScholarshipEditor implements IEditorOperations {
	public readonly formModel = ref<ScholarshipFormModel>();

	public lastErrorMessage: Ref<string | undefined> = ref<string | undefined>();
	public isFormAvailable: Ref<boolean | undefined> = ref<boolean>(false);

	private persistedScholarship: Scholarship | undefined;

	public get providedScholarshipId(): string | undefined {
		return PlatformIntegration.instance.context.resourceId;
	}

	public get scholarshipId(): string {
		if (this.providedScholarshipId === undefined || this.notHasScholarshipId) {
			throw new Error('The scholarship ID id not available in create mode.');
		}

		return this.providedScholarshipId;
	}

	public get hasScholarshipId(): boolean {
		return this.providedScholarshipId !== undefined && this.providedScholarshipId.length > 0;
	}

	public get notHasScholarshipId(): boolean {
		return !this.hasScholarshipId;
	}

	public get isCreateMode(): boolean {
		return !this.hasScholarshipId;
	}

	public get isUpdateMode(): boolean {
		return this.hasScholarshipId;
	}

	public beforeMount(): void {
		EditorIntegration.instance.editor = this;
		EditorIntegration.instance.formsReset();
	}

	public async mounted(): Promise<void> {
		if (this.isCreateMode) {
			this.reset();
			this.isFormAvailable.value = true;

			EditorIntegration.instance.notifyReady();
		} else {
			this.persistedScholarship = await this.retrieveScholarship();

			if (this.persistedScholarship !== undefined) {
				this.prefill();
				this.isFormAvailable.value = true;
			}

			EditorIntegration.instance.notifyReady();
		}
	}

	public getTitle(): string {
		if (this.isUpdateMode) {
			return this.persistedScholarship?.title ?? 'N/A';
		}

		return `New scholarship`;
	}

	public getPublicUrl(): string {
		if (this.isUpdateMode) {
			return `${CONFIG.MASTERS_PORTAL_URI}/scholarships/${this.scholarshipId}/`;
		}

		return '#';
	}

	public async save(): Promise<void> {
		if (this.isCreateMode) {
			await this.create();
		}

		if (this.isUpdateMode) {
			await this.update();
		}
	}

	public isPublished(): boolean {
		const formModel = toRaw(this.formModel.value);
		return formModel?.isPublished.value ?? false;
	}

	public publish(): void {
		const formModel = toRaw(this.formModel.value);
		if (formModel) {
			formModel.isPublished.value = true;
		}
	}

	public unPublish(): void {
		const formModel = toRaw(this.formModel.value);
		if (formModel) {
			formModel.isPublished.value = false;
		}
	}

	private async retrieveScholarship(): Promise<Scholarship | undefined> {
		if (this.notHasScholarshipId) {
			return undefined;
		}

		try {
			return await ScholarshipPresenter.retrieve(this.scholarshipId);
		} catch (error) {
			this.lastErrorMessage.value = (error as Error).message;

			return undefined;
		}
	}

	private async create(): Promise<void> {
		const formModel = toRaw(this.formModel.value);
		if (formModel === undefined) {
			return;
		}

		try {
			const scholarshipInfo = toScholarshipInfo(formModel);
			const scholarshipId = await ScholarshipService.create(scholarshipInfo);

			this.lastErrorMessage.value = undefined;
			this.reset();

			EditorIntegration.instance.notifyFormClean();
			PlatformIntegration.instance.router.toModify(scholarshipId);
		} catch (error) {
			this.lastErrorMessage.value = (error as Error).message;

			throw error;
		}
	}

	private async update(): Promise<void> {
		const formModel = toRaw(this.formModel.value);
		if (formModel === undefined || this.notHasScholarshipId) {
			return;
		}

		try {
			const scholarshipInfo = toScholarshipInfo(formModel);
			const presence = toPresence(formModel);

			this.persistedScholarship = await ScholarshipService.update(this.scholarshipId, scholarshipInfo, presence);

			this.prefill();
			this.lastErrorMessage.value = undefined;
		} catch (error) {
			this.lastErrorMessage.value = (error as Error).message;

			throw error;
		}
	}

	private reset(): void {
		this.formModel.value = new ScholarshipFormModel();
	}

	private prefill(): void {
		if (this.persistedScholarship) {
			this.formModel.value = ScholarshipFormModel.fromScholarship(this.persistedScholarship);
		}
	}
}
